import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { store } from '../store';
import { EndPoints } from '../../axios/endPoints';
import { axiosInstance } from '../../axios/axiosHandler';

export interface SubscriptionItem {
  name: string;
  value: string;
}

export interface CurrentSubscription {
  name: string;
  items: SubscriptionItem[];
}

export interface Auth {
  loading: boolean;
  user: {
    email: string | null;
    role: string | null;
  };
  token: string | null;
  currentSubscription: CurrentSubscription | null;
}

interface AuthProps {
  success: boolean;
  expiration: string | null;
  user: string | null;
  userRoles: [string] | null;
  token: string | null;
  currentSubscription?: CurrentSubscription | null;
}

export interface LogInData {
  email: string;
  password: string;
  remember: boolean;
}

export function authHeader() {
  const token = authToken();
  const isLoggedIn = !!token;
  if (isLoggedIn) {
    return {
      headers: { Authorization: `Bearer ${token}` },
    };
  } else {
    return {};
  }
}

function authToken() {
  return store.getState().auth?.token;
}

const defaultAuthState: Auth = {
  loading: false,
  user: {
    email: null,
    role: null,
  },
  token: null,
  currentSubscription: null,
};

function loadInitialAuthState(): Auth {
  const savedAuth = localStorage.getItem('auth');
  if (!savedAuth) {
    return defaultAuthState;
  }

  try {
    const parsed = JSON.parse(savedAuth);
    return { ...defaultAuthState, ...parsed };
  } catch {
    return defaultAuthState;
  }
}

export const performLogIn = createAsyncThunk(
  'LogIn',
  async (payload: LogInData, thunkAPI) => {
    try {
      const response = await axiosInstance.post(EndPoints.LOG_IN, {
        username: payload.email,
        password: payload.password,
        remember: payload.remember,
      });
      return await response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? 'Something went wrong.'
      );
    }
  }
);

export const authSlice = createSlice({
  name: 'auth',
  initialState: loadInitialAuthState(),
  reducers: {
    updateCurrentSubscription: (state, action: PayloadAction<CurrentSubscription | null>) => {
      const nextState: Auth = {
        ...state,
        currentSubscription: action.payload ?? null,
      };

      localStorage.setItem('auth', JSON.stringify(nextState));
      return nextState;
    },
    handleLogOut: (state) => {
      state = {
        ...defaultAuthState,
      };

      localStorage.setItem('auth', JSON.stringify(state));
      return state;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(performLogIn.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(performLogIn.rejected, () => {
      const nextState: Auth = {
        ...defaultAuthState,
      };

      return nextState;
    });

    builder.addCase(performLogIn.fulfilled, (state, action) => {
      const { success, user, userRoles, token, currentSubscription } =
        action.payload as AuthProps;

      if (success) {
        state = {
          loading: false,
          user: {
            email: user ?? null,
            role: userRoles?.[0] ?? null,
          },
          token: token ?? null,
          currentSubscription: currentSubscription ?? null,
        };

        localStorage.setItem('auth', JSON.stringify(state));

        return state;
      } else
        state = {
          ...defaultAuthState,
        };

      return state;
    });
  },
});

export const { handleLogOut, updateCurrentSubscription } = authSlice.actions;

export const authSelector = (state: RootState) => state.auth;
